<template>
  <a-dropdown
    v-model:open="dropdownVisible"
    :trigger="['click']"
    placement="bottomLeft"
  >
    <slot />
    <template #overlay>
      <div class="column_header_dropdown_content">
        <div class="flex flex-col w-[220px]">
          <div v-if="isEditingOptions" class="flex flex-col">
            <div class="flex items-center gap-2 p-5">
              <div
                class="flex items-center justify-center cursor-pointer icon_hover_effect p-1"
                @click.stop="exitEditMode"
              >
                <ThemeImage
                  :icon="imageNames.BACK_ARROW_WHITE"
                  alt="back_arrow"
                  class="size-4"
                />
              </div>
              <span class="text-sm text_color font-semibold"
                >Edit property</span
              >
            </div>

            <div class="flex flex-col px-3 pb-3 w-full" @click.stop>
              <span class="secondary_color text-[13px] mb-2 px-2">OPTIONS</span>

              <SkeletonLoader v-if="isLoadingOptions" page="manage_columns" />

              <div v-if="!isLoadingOptions">
                <div
                  v-if="isAddingOption"
                  class="flex items-center gap-2 mb-2"
                  @click.stop
                >
                  <input
                    ref="newOptionInput"
                    v-model="newOptionValue"
                    type="text"
                    class="flex-1 outline-none input_medium text_color text-sm px-3 py-2"
                    placeholder="Enter option name"
                    @keydown="handleNewOptionKeydown"
                    @blur="handleNewOptionBlur"
                    @click.stop
                  />
                </div>

                <div
                  v-if="!isAddingOption"
                  class="flex items-center gap-2 cursor-pointer dropdown_item mb-2"
                  @click.stop="startAddingOption"
                >
                  <ThemeImage
                    :icon="imageNames.PLUS"
                    alt="plus_icon"
                    class="size-4"
                  />
                  <span class="text-sm text_color">Add an option</span>
                </div>

                <div
                  class="flex flex-col gap-2 max-h-[300px] overflow-y-auto custom_scrollbar"
                >
                  <div
                    v-for="element in options"
                    :key="element.id"
                    class="flex items-center gap-2"
                    :data-option-id="element.id"
                    @click.stop
                  >
                    <div
                      class="flex-1 px-3 py-2 flex items-center"
                      :class="{ input_medium: activeOptionId === element.id }"
                    >
                      <input
                        v-model="element.value"
                        type="text"
                        class="outline-none bg-transparent text_color text-sm font-normal w-[80%]"
                        :placeholder="
                          element.isNew ? 'Enter option' : element.originalValue
                        "
                        :disabled="!canModifyExistingOptions"
                        @keydown="(e) => handleOptionKeydown(e, element)"
                        @blur="() => handleOptionBlur(element)"
                        @focus="() => (activeOptionId = element.id)"
                        @click.stop
                      />
                    </div>

                    <ThemeImage
                      v-if="canModifyExistingOptions && !isLoadingOptions"
                      :icon="imageNames.CROSS_GREY"
                      alt="cross_mark"
                      class="text-sm text_color cursor-pointer size-4 flex-shrink-0"
                      @click="deleteOptionDirectly(element.id)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="flex flex-col">
            <div class="p-2 border_bottom" @click.stop>
              <a-input
                v-model:value="propertyName"
                class="input_medium"
                placeholder="Property Name"
                @input="validatePropertyName"
                @blur="handlePropertyNameBlur"
                @keydown="handlePropertyNameKeydown"
                :status="validationError ? 'error' : ''"
                :disabled="
                  !props.isDynamicObject ||
                  props.propertyMeta?.is_system ||
                  isReadOnlySchema ||
                  isUpdating
                "
                :loading="isUpdating"
              />
              <div
                v-if="validationError"
                class="error_message_color text-xs mt-1"
              >
                {{ validationError }}
              </div>
            </div>
            <div
              class="p-2"
              :class="{ border_bottom: showColumnEdit && (canEditProperty || canDeleteProperty) }"
            >
              <div
                class="flex items-center gap-2 p-2 cursor-pointer dropdown_item"
                :class="{ selected: isCurrentSort('asc') }"
                @click="handleSort('asc')"
              >
                <ThemeImage
                  :icon="imageNames.SORT_ASCENDING_NEUTRAL"
                  alt="sort_ascending_arrow"
                  class="size-4"
                />
                <span class="text-sm text_color">Sort ascending</span>
              </div>
              <div
                class="flex items-center gap-2 p-2 cursor-pointer dropdown_item"
                :class="{ selected: isCurrentSort('desc') }"
                @click="handleSort('desc')"
              >
                <ThemeImage
                  :icon="imageNames.SORT_DESCENDING_NEUTRAL"
                  alt="sort_descending_arrow"
                  class="size-4"
                />
                <span class="text-sm text_color">Sort descending</span>
              </div>
              <a-tooltip :title="freezeTooltipTitle" placement="right">
                <div
                  class="flex items-center gap-2 p-2 dropdown_item"
                  :class="{
                    'cursor-pointer': isFreezeEnabled,
                    'cursor-not-allowed opacity-50': !isFreezeEnabled,
                  }"
                  @click="isFreezeEnabled && handleFreezeColumn()"
                >
                  <ThemeImage
                    :icon="isColumnFrozen ? imageNames.UNPIN : imageNames.PIN"
                    :alt="isColumnFrozen ? 'unpin_icon' : 'pin_icon'"
                    class="size-4"
                  />
                  <span class="text-sm text_color">{{
                    isColumnFrozen ? "Unfreeze columns" : "Freeze"
                  }}</span>
                </div>
              </a-tooltip>
            </div>

            <PropertyEditDropdown
              v-if="showColumnEdit && (canEditProperty || canDeleteProperty)"
              :property-meta="propertyMeta"
              :entity-type="entityType"
              :project-id="projectId"
              :service="service"
              :is-dynamic-object="isDynamicObject"
              :number-attributes="numberAttributes"
              :can-edit-property="canEditProperty"
              :can-delete-property="canDeleteProperty"
              @property-updated="handlePropertyUpdated"
              @edit="handleEdit"
              @delete="handleHide"
              @close="handleClose"
            />
          </div>
        </div>
      </div>
    </template>
  </a-dropdown>
</template>

<script setup>
import { computed, ref, watch, nextTick, onMounted, onUnmounted } from "vue";
import { message } from "ant-design-vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import CRMService from "@/src/api/CRMService.js";
import TicketsService from "@/src/api/TicketsService.js";
import ProjectService from "@/src/api/ProjectService.js";
import SkeletonLoader from "../SkeletonLoader.vue";
import PropertyEditDropdown from "./PropertyEditDropdown.vue";
import { sortByCreatedAt } from "@/src/utils/utils.js";
import { ATTRIBUTE_TYPES, isUUID, isReadonlySchema } from "@/src/constants/crmConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants";
import { useStore } from "vuex";
const store = useStore();
const props = defineProps({
  column: {
    type: Object,
    required: true,
  },
  propertyMeta: {
    type: Object,
    default: () => null,
  },
  frozenColumns: {
    type: Array,
    default: () => [],
  },
  allColumns: {
    type: Array,
    default: () => [],
  },
  entityType: {
    type: String,
    default: "",
  },
  projectId: {
    type: [String, Number],
    default: null,
  },
  isDynamicObject: {
    type: Boolean,
    default: false,
  },
  currentSorts: {
    type: Object,
    default: () => ({}),
  },
  numberAttributes: {
    type: Array,
    default: () => [],
  },
  showColumnEdit: {
    type: Boolean,
    default: true,
  },
  service: {
    type: String,
    default: null,
  },
});

const emit = defineEmits([
  "sort",
  "edit",
  "close",
  "delete-property",
  "property-updated",
  "current-sort-changed",
  "freeze-column",
  "unfreeze-column",
]);

const dropdownVisible = ref(false);
const propertyName = ref(props.propertyMeta?.name || props.column?.title || "");
const originalPropertyName = ref(
  props.propertyMeta?.name || props.column?.title || "",
);
const validationError = ref("");
const isUpdating = ref(false);
const isEditingOptions = ref(false);
const options = ref([]);
const newOptionValue = ref("");
const isAddingOption = ref(false);
const newOptionInput = ref(null);
const isLoadingOptions = ref(false);
const activeOptionId = ref(null);
const isOptionsEditableFromAPI = ref(null);
const windowWidth = ref(window.innerWidth);

const isTickets = computed(() => props.entityType === "tickets");
const isTasks = computed(() => props.entityType === "tasks");

// Finance/invoice objects — disable property name/edit/delete in the table header.
// Scoped to the owning service so CRM-scoped custom objects sharing a slug stay editable.
const isReadOnlySchema = computed(() =>
  isReadonlySchema(props.entityType, props.service),
);

const refreshSchemaStore = async () => {
  const entitySlug = isTickets.value
    ? entityTypes.TICKETS
    : isTasks.value
      ? entityTypes.TASKS
      : props.entityType;
  await store.dispatch("schema/refreshSchema", {
    slug: entitySlug,
    projectId: isTasks.value ? props.projectId : null,
    service: props.service || null,
  });
};

const validatePropertyName = () => {
  validationError.value = "";

  if (!propertyName.value.trim()) {
    validationError.value = "Property name is required.";
    return false;
  }

  if (propertyName.value.length > 50) {
    validationError.value =
      "Character limit exceeded. Maximum 50 characters allowed.";
    return false;
  }

  const validNameRegex = /^[a-zA-Z0-9\s.,!?()-]+$/;
  if (propertyName.value && !validNameRegex.test(propertyName.value)) {
    validationError.value =
      "Only alphanumeric, spaces and basic punctuation allowed.";
    return false;
  }

  if (propertyName.value && !validationError.value) {
    const schema = store.getters["schema/getSchemaBySlug"](
      props.entityType,
      isTasks.value ? props.projectId : null,
    );
    const existingProperties = schema?.attributes || [];
    const normalizedNewName = propertyName.value.trim().toLowerCase();
    const isDuplicate = existingProperties.some((attr) => {
      const existingName = attr.name?.trim().toLowerCase();
      return (
        existingName === normalizedNewName && attr.id !== props.propertyMeta?.id
      );
    });
    if (isDuplicate) {
      validationError.value = "A property with this name already exists.";
      return false;
    }
  }

  return true;
};

const hasPropertyNameChanged = () => {
  return propertyName.value.trim() !== originalPropertyName.value.trim();
};

const updatePropertyName = async () => {
  if (
    !validatePropertyName() ||
    !hasPropertyNameChanged() ||
    isUpdating.value
  ) {
    return;
  }

  if (
    !props.isDynamicObject ||
    !props.entityType ||
    !props.propertyMeta?.slug
  ) {
    console.error("Missing required data for property update");
    return;
  }

  try {
    isUpdating.value = true;

    const payload = {
      ...props.propertyMeta,
      name: propertyName.value.trim(),
      type: props.propertyMeta.type,
      created_by_id: Number(localStorage.getItem("userId")) || 1,
    };

    if (props.propertyMeta.type === ATTRIBUTE_TYPES.MULTI_SELECT) {
      payload.is_multi_value = true;
    }

    if (
      props.propertyMeta.datatype === ATTRIBUTE_TYPES.FORMULA &&
      props.propertyMeta.formula
    ) {
      if (
        typeof props.propertyMeta.formula === "object" &&
        props.propertyMeta.formula.original
      ) {
        payload.formula = props.propertyMeta.formula.original;
      } else if (typeof props.propertyMeta.formula === "string") {
        payload.formula = props.propertyMeta.formula;
      }
    }

    let response;
    if (isTickets.value) {
      response = await TicketsService.editSchemaAttribute(
        props.propertyMeta.slug,
        payload,
      );
    } else if (isTasks.value) {
      if (!props.projectId) {
        message.error("Project ID is required for task operations");
        return;
      }
      response = await ProjectService.tasks.editTaskSchemaAttribute(
        props.projectId,
        props.propertyMeta.slug,
        payload,
      );
    } else {
      response = await CRMService.updateSchemaObjectAttribute(
        props.entityType,
        props.propertyMeta.slug,
        payload,
        props.service,
      );
    }

    if (response.success) {
      await refreshSchemaStore();

      message.success({
        content: `Property "${originalPropertyName.value}" updated to "${propertyName.value}" successfully`,
        duration: 3,
      });

      originalPropertyName.value = propertyName.value.trim();

      emit("property-updated", {
        column: props.column,
        propertyMeta: props.propertyMeta,
        newName: propertyName.value.trim(),
        response: response,
        isOptionChange: false,
      });
    } else {
      let errorMsg = "Failed to update property name";
      if (
        response.errors &&
        Array.isArray(response.errors) &&
        response.errors.length > 0
      ) {
        errorMsg = response.errors[0];
      } else if (response.error) {
        errorMsg = response.error;
      } else if (response.message) {
        errorMsg = response.message;
      }

      message.error(errorMsg);
      propertyName.value = originalPropertyName.value;
    }
  } catch (error) {
    console.error("Error updating property name:", error);
    message.error("Failed to update property name");
    propertyName.value = originalPropertyName.value;
  } finally {
    isUpdating.value = false;
  }
};

const handlePropertyNameBlur = () => {
  if (hasPropertyNameChanged() && validatePropertyName()) {
    updatePropertyName();
  } else if (!validatePropertyName()) {
    propertyName.value = originalPropertyName.value;
    validationError.value = "";
  }
};

const handlePropertyNameKeydown = (event) => {
  if (event.key === "Enter") {
    event.preventDefault();
    if (hasPropertyNameChanged() && validatePropertyName()) {
      updatePropertyName();
    }
  } else if (event.key === "Escape") {
    event.preventDefault();
    propertyName.value = originalPropertyName.value;
    validationError.value = "";
    event.target.blur();
  }
};

const canEditProperty = computed(() => {
  if (isReadOnlySchema.value) return false;
  if (props.propertyMeta?.is_options_internal === true) return false;

  const datatype = props.propertyMeta?.datatype;
  return (
    datatype === ATTRIBUTE_TYPES.SELECT ||
    datatype === ATTRIBUTE_TYPES.MULTI_SELECT ||
    datatype === ATTRIBUTE_TYPES.FORMULA ||
    datatype === ATTRIBUTE_TYPES.PROPERTY_SYNC ||
    datatype === ATTRIBUTE_TYPES.RECORD ||
    datatype === ATTRIBUTE_TYPES.SELECT_TIMESTAMP ||
    datatype === ATTRIBUTE_TYPES.ACTION
  );
});

const canModifyExistingOptions = computed(() => {
  if (isOptionsEditableFromAPI.value !== null) return isOptionsEditableFromAPI.value;
  return props.propertyMeta?.is_options_editable !== false;
});

const isSelectOrMultiSelect = computed(() => {
  const datatype = props.propertyMeta?.datatype;
  return (
    datatype === ATTRIBUTE_TYPES.SELECT ||
    datatype === ATTRIBUTE_TYPES.MULTI_SELECT
  );
});

const canDeleteProperty = computed(() => {
  if (isReadOnlySchema.value) return false;
  if (!props.isDynamicObject || !props.propertyMeta) return false;
  if (props.propertyMeta.is_primary || props.propertyMeta.is_system || props.propertyMeta.workspace_id === 0) return false;
  return isUUID(props.propertyMeta.id);
});

const isColumnFrozen = computed(() => {
  if (!props.frozenColumns?.length) {
    return false;
  }

  const lastFrozenColumn = props.frozenColumns[props.frozenColumns.length - 1];
  return props.column.key === lastFrozenColumn;
});

const canFreezeThisColumn = computed(() => {
  if (isColumnFrozen.value || !props.allColumns?.length) {
    return true;
  }

  const columnIndex = props.allColumns.findIndex(
    (col) => col === props.column.key,
  );

  if (columnIndex === -1) {
    return true;
  }

  try {
    const tableContainer = document.querySelector(
      '.ant-table-wrapper, .simple-table-wrapper, [class*="table"]',
    );
    if (!tableContainer) {
      const freezePercentage =
        ((columnIndex + 1) / props.allColumns.length) * 100;
      return freezePercentage <= 80;
    }

    const containerWidth = tableContainer.clientWidth;

    const headers = Array.from(tableContainer.querySelectorAll("th"));
    let totalFrozenWidth = 0;

    for (let i = 0; i <= columnIndex && i < headers.length; i++) {
      const header = headers[i];
      if (header && !header.classList.contains("ant-table-selection-column")) {
        totalFrozenWidth += header.offsetWidth || 150;
      }
    }

    return totalFrozenWidth <= containerWidth;
  } catch (error) {
    console.warn("Error calculating freeze width:", error);
    const freezePercentage =
      ((columnIndex + 1) / props.allColumns.length) * 100;
    return freezePercentage <= 80;
  }
});

const isFreezeEnabled = computed(() => {
  return canFreezeThisColumn.value || isColumnFrozen.value;
});

const freezeTooltipTitle = computed(() => {
  return !canFreezeThisColumn.value && !isColumnFrozen.value
    ? "Insufficient space available to freeze columns"
    : "";
});

const handleFreezeColumn = () => {
  if (isColumnFrozen.value) {
    emit("unfreeze-column", {
      column: props.column,
    });
  } else {
    emit("freeze-column", {
      column: props.column,
    });
  }
  dropdownVisible.value = false;
  emit("close");
};

const handleSort = (direction) => {
  const newDirection = isCurrentSort(direction) ? null : direction;
  emit("sort", {
    column: props.column,
    direction: newDirection,
  });
  dropdownVisible.value = false;
};

const handleEdit = () => {
  // Only handle select/multi-select inline editing here
  // Other types are handled by PropertyEditDropdown component
  if (isSelectOrMultiSelect.value) {
    enterEditMode();
  } else {
    emit("edit", {
      column: props.column,
    });
    dropdownVisible.value = false;
    emit("close");
  }
};

const enterEditMode = async () => {
  isEditingOptions.value = true;
  await loadOptions();
};

const exitEditMode = () => {
  isEditingOptions.value = false;
  isAddingOption.value = false;
  newOptionValue.value = "";
  isOptionsEditableFromAPI.value = null;
};

const loadOptions = async () => {
  isOptionsEditableFromAPI.value = null;
  if (
    !props.isDynamicObject ||
    !props.entityType ||
    !props.propertyMeta?.slug
  ) {
    loadOptionsFromPropertyMeta();
    return;
  }

  const datatype = props.propertyMeta?.datatype;
  if (
    datatype !== ATTRIBUTE_TYPES.SELECT &&
    datatype !== ATTRIBUTE_TYPES.MULTI_SELECT &&
    props.propertyMeta?.type !== ATTRIBUTE_TYPES.SELECT &&
    props.propertyMeta?.type !== ATTRIBUTE_TYPES.MULTI_SELECT
  ) {
    options.value = [];
    return;
  }

  try {
    isLoadingOptions.value = true;
    let response;

    if (isTickets.value) {
      response = await TicketsService.getSchemaAttributeDetails(
        props.propertyMeta.slug,
      );
    } else if (isTasks.value) {
      if (!props.projectId) {
        message.error("Project ID is required for task operations");
        loadOptionsFromPropertyMeta();
        return;
      }
      response = await ProjectService.tasks.getTaskAttributeOptions(
        props.projectId,
        props.propertyMeta.slug,
      );
    } else {
      response = await CRMService.getPraticularAttributeDetails(
        props.entityType,
        props.propertyMeta.slug,
        props.service,
      );
    }

    if (response.success) {
      if (
        response.attribute?.is_options_editable != null &&
        response.attribute?.slug === props.propertyMeta?.slug
      ) {
        isOptionsEditableFromAPI.value = Boolean(response.attribute.is_options_editable);
      }
      const optionsArray = response.options || response.attribute?.structured_options || response.attribute?.options;

      if (optionsArray && Array.isArray(optionsArray)) {
        const isObjectArray = optionsArray.length > 0 && typeof optionsArray[0] === 'object';
        const sortedOptions = isObjectArray ? sortByCreatedAt(optionsArray, "desc") : optionsArray;

        options.value = sortedOptions.map((optionObj, index) => {
          const optValue = typeof optionObj === 'string'
            ? optionObj
            : (optionObj.option || optionObj.label || optionObj.value || '');
          const optId = typeof optionObj === 'object' ? (optionObj.id || null) : null;
          return {
            id: index + 1,
            value: optValue,
            isNew: false,
            originalValue: optValue,
            apiId: optId,
          };
        });
      } else {
        options.value = [];
      }
    } else {
      console.error("Failed to fetch attribute options:", response);
      loadOptionsFromPropertyMeta();
    }
  } catch (error) {
    console.error("Error loading options from API:", error);
    loadOptionsFromPropertyMeta();
  } finally {
    isLoadingOptions.value = false;
  }
};

const loadOptionsFromPropertyMeta = () => {
  if (
    props.propertyMeta?.options &&
    Array.isArray(props.propertyMeta.options)
  ) {
    options.value = props.propertyMeta.options.map((optionObj, index) => ({
      id: index + 1,
      value:
        typeof optionObj === "string"
          ? optionObj
          : optionObj.option || optionObj.value || String(optionObj),
      isNew: false,
      originalValue:
        typeof optionObj === "string"
          ? optionObj
          : optionObj.option || optionObj.value || String(optionObj),
      apiId: typeof optionObj === "object" ? optionObj.id : null,
    }));
  } else {
    options.value = [];
  }
};

const startAddingOption = () => {
  isAddingOption.value = true;
  nextTick(() => {
    if (newOptionInput.value) {
      newOptionInput.value.focus();
    }
  });
};

const addNewOptionViaAPI = async () => {
  if (!newOptionValue.value.trim()) {
    isAddingOption.value = false;
    return;
  }

  if (props.isDynamicObject && props.entityType && props.propertyMeta?.slug) {
    try {
      let response;

      if (isTickets.value) {
        response = await TicketsService.addAttributeOptions(
          props.propertyMeta.slug,
          { options: [newOptionValue.value.trim()] },
        );
      } else if (isTasks.value) {
        if (!props.projectId) {
          message.error("Project ID is required for task operations");
          return;
        }
        response = await ProjectService.tasks.addTaskAttributeOptions(
          props.projectId,
          props.propertyMeta.slug,
          { options: [newOptionValue.value.trim()] },
        );
      } else {
        response = await CRMService.addAttributeOption(
          props.entityType,
          props.propertyMeta.slug,
          { options: [newOptionValue.value.trim()] },
          props.service,
        );
      }

      if (response.success) {
        await refreshSchemaStore();

        message.success(
          `Option "${newOptionValue.value.trim()}" added successfully`,
        );

        const newId =
          options.value.length > 0
            ? Math.max(...options.value.map((opt) => opt.id)) + 1
            : 1;

        const newOption = {
          id: newId,
          value: newOptionValue.value.trim(),
          isNew: false,
          originalValue: newOptionValue.value.trim(),
          apiId: null,
        };

        if (response.options && response.options.length > 0) {
          const serverOption = response.options[0];
          newOption.apiId = serverOption.id;
        }

        options.value.push(newOption);
        newOptionValue.value = "";
        isAddingOption.value = false;

        emit("property-updated", {
          column: props.column,
          propertyMeta: props.propertyMeta,
          newName: props.propertyMeta.name,
          response: response,
          isOptionChange: true,
        });
      } else {
        message.error("Failed to add option");
        console.error("API returned error:", response);
      }
    } catch (error) {
      console.error("Error adding option:", error);
      message.error("Failed to add option");
    }
  } else {
    addNewOptionFallback();
  }
};

const addNewOptionFallback = () => {
  if (!newOptionValue.value.trim()) {
    isAddingOption.value = false;
    return;
  }

  const newId =
    options.value.length > 0
      ? Math.max(...options.value.map((opt) => opt.id)) + 1
      : 1;

  const newOption = {
    id: newId,
    value: newOptionValue.value.trim(),
    isNew: true,
    originalValue: null,
    apiId: null,
  };

  options.value.push(newOption);
  newOptionValue.value = "";
  isAddingOption.value = false;

  nextTick(() => {
    const newOptionElement = document.querySelector(
      `[data-option-id="${newId}"] input`,
    );
    if (newOptionElement) {
      newOptionElement.focus();
    }
  });
};

const deleteOptionDirectly = async (id) => {
  const optionToDeleteObj = options.value.find((o) => o.id === id);
  if (!optionToDeleteObj) {
    return;
  }

  try {
    if (
      props.isDynamicObject &&
      !optionToDeleteObj.isNew &&
      optionToDeleteObj.apiId
    ) {
      if (isTickets.value) {
        await TicketsService.deleteAttributeOption(optionToDeleteObj.apiId);
      } else if (isTasks.value) {
        if (!props.projectId) {
          message.error("Project ID is required for task operations");
          return;
        }
        await ProjectService.tasks.deleteTaskAttributeOption(
          props.projectId,
          optionToDeleteObj.apiId,
        );
      } else {
        await CRMService.deleteAttributeOption(
          optionToDeleteObj.apiId,
          props.propertyMeta?.slug,
          props.service,
        );
      }
      message.success(
        `Option "${optionToDeleteObj.value}" deleted successfully`,
      );
    }

    options.value = options.value.filter((opt) => opt.id !== id);

    emit("property-updated", {
      column: props.column,
      propertyMeta: props.propertyMeta,
      newName: props.propertyMeta.name,
      response: { success: true },
      isOptionChange: true,
    });
  } catch (error) {
    console.error("Error deleting option:", error);
    message.error("Failed to delete option");
  }
};

const handleOptionDeleted = (optionId) => {
  options.value = options.value.filter((opt) => opt.id !== optionId);
};

const handleNewOptionKeydown = async (event) => {
  if (event.key === "Enter") {
    event.preventDefault();
    event.stopPropagation();
    await addNewOptionViaAPI();
  } else if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    newOptionValue.value = "";
    isAddingOption.value = false;
  }
};

const handleNewOptionBlur = async () => {
  if (newOptionValue.value.trim()) {
    await addNewOptionViaAPI();
  } else {
    isAddingOption.value = false;
  }
};

const handleOptionKeydown = (event, option) => {
  if (event.key === "Enter") {
    event.preventDefault();
    event.stopPropagation();
    handleOptionBlur(option);
  } else if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    option.value = option.originalValue;
  }
};

const handleOptionBlur = async (option) => {
  activeOptionId.value = null;

  if (option.isNew) {
    if (!option.value.trim()) {
      options.value = options.value.filter((opt) => opt.id !== option.id);
      return;
    }

    if (props.isDynamicObject && props.entityType && props.propertyMeta?.slug) {
      try {
        let response;

        if (isTickets.value) {
          response = await TicketsService.addAttributeOptions(
            props.propertyMeta.slug,
            { options: [option.value.trim()] },
          );
        } else if (isTasks.value) {
          if (!props.projectId) {
            message.error("Project ID is required for task operations");
            options.value = options.value.filter((opt) => opt.id !== option.id);
            return;
          }
          response = await ProjectService.tasks.addTaskAttributeOptions(
            props.projectId,
            props.propertyMeta.slug,
            { options: [option.value.trim()] },
          );
        } else {
          response = await CRMService.addAttributeOption(
            props.entityType,
            props.propertyMeta.slug,
            { options: [option.value.trim()] },
            props.service,
          );
        }

        if (response.success) {
          await refreshSchemaStore();

          message.success(`Option "${option.value.trim()}" added successfully`);
          if (response.options && response.options.length > 0) {
            const serverOption = response.options[0];
            option.apiId = serverOption.id;
            option.isNew = false;
            option.originalValue = option.value.trim();
          }

          emit("property-updated", {
            column: props.column,
            propertyMeta: props.propertyMeta,
            newName: props.propertyMeta.name,
            response: response,
            isOptionChange: true,
          });
        } else {
          message.error("Failed to add option");
          options.value = options.value.filter((opt) => opt.id !== option.id);
          return;
        }
      } catch (error) {
        console.error("Error adding option:", error);
        message.error("Failed to add option");
        options.value = options.value.filter((opt) => opt.id !== option.id);
        return;
      }
    }
  } else if (option.value.trim() !== option.originalValue) {
    if (!option.value.trim()) {
      message.error("Option value cannot be empty");
      option.value = option.originalValue;
      return;
    }
    if (props.isDynamicObject && option.apiId) {
      try {
        let response;

        if (isTickets.value) {
          response = await TicketsService.editAttributeOption(option.apiId, {
            option: option.value.trim(),
          });
        } else if (isTasks.value) {
          if (!props.projectId) {
            message.error("Project ID is required for task operations");
            option.value = option.originalValue;
            return;
          }
          response = await ProjectService.tasks.updateTaskAttributeOption(
            props.projectId,
            option.apiId,
            { option: option.value.trim() },
          );
        } else {
          response = await CRMService.editAttributeOption(
            option.apiId,
            { option: option.value.trim() },
            props.propertyMeta?.slug,
            props.service,
          );
        }

        if (response.success) {
          await refreshSchemaStore();

          message.success(
            `Option "${option.originalValue}" updated successfully`,
          );
          option.originalValue = option.value.trim();

          emit("property-updated", {
            column: props.column,
            propertyMeta: props.propertyMeta,
            newName: props.propertyMeta.name,
            response: response,
            isOptionChange: true,
          });
        } else {
          message.error("Failed to update option");
          option.value = option.originalValue;
        }
      } catch (error) {
        console.error("Error updating option:", error);
        message.error("Failed to update option");
        option.value = option.originalValue;
      }
    }
  }
};

const handleHide = () => {
  emit("delete-property", {
    column: props.column,
    propertyMeta: props.propertyMeta,
  });
  dropdownVisible.value = false;
  emit("close");
};

// Handler for property updates from PropertyEditDropdown
const handlePropertyUpdated = (data) => {
  emit("property-updated", data);
  // Don't close dropdown for action changes - user may want to continue editing
  if (!data.isActionChange) {
    dropdownVisible.value = false;
  }
};

// Handler for closing the dropdown from PropertyEditDropdown
const handleClose = () => {
  dropdownVisible.value = false;
  emit("close");
};

const toggleDropdown = () => {
  dropdownVisible.value = !dropdownVisible.value;
};

const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});

defineExpose({
  toggleDropdown,
  handleOptionDeleted,
});

const isCurrentSort = (direction) => {
  return Object.values(props.currentSorts).some(
    (sort) => sort.field === props.column.key && sort.order === direction,
  );
};

const currentSortDirection = computed(() => {
  const sortObj = Object.values(props.currentSorts).find(
    (sort) => sort.field === props.column.key,
  );
  return sortObj ? sortObj.order : null;
});

watch(
  () => props.propertyMeta?.name || props.column?.title,
  (newName) => {
    if (newName && newName !== propertyName.value) {
      propertyName.value = newName;
      originalPropertyName.value = newName;
    }
  },
  { immediate: true },
);

watch(
  () => props.propertyMeta?.id,
  () => {
    isOptionsEditableFromAPI.value = null;
  },
);

watch(
  () => props.propertyMeta?.options,
  (newOptions) => {
    if (isEditingOptions.value && newOptions) {
      loadOptions();
    }
  },
  { deep: true },
);

watch(
  () => currentSortDirection.value,
  (newDirection) => {
    emit("current-sort-changed", {
      column: props.column,
      direction: newDirection,
    });
  },
  { immediate: true },
);
</script>

<style>
.column_header_dropdown_content {
  background: var(--dropdown-bg);
  border-radius: 8px;
  box-shadow: var(--dropdown-boxshadow);
  width: fit-content;
}
</style>
